<template>
  <b-container>
    <b-row>
      <b-col cols="12">
        <b-row class="justify-content-between mb-3">
          <b-col cols="3" sm="6" md="3" class="d-flex align-items-end">
            <div>
              <label for="input">Rechercher</label>
              <b-input-group>
                <template #append>
                  <b-input-group-text
                    v-if="!hasSearchInputBeenReseted || isSearchInputFocused || searchParams"
                    type="button"
                    @click="resetSearch"
                  >
                    <font-awesome-icon icon="times" size="sm"></font-awesome-icon>
                  </b-input-group-text>
                </template>

                <b-form-input
                  id="input"
                  v-model="searchParams"
                  placeholder="Tapez pour rechercher"
                  @focus="focusSearch"
                  @blur="unfocusSearch"
                  @keyup.enter="search"
                />
              </b-input-group>
              <small class="text-secondary ml-2"
                >Utilisez la touche Entrée pour lancer la recherche</small
              >
            </div>
          </b-col>
          <b-col cols="3" sm="6" md="3">
            <label>par page</label>
            <b-select
              v-model="pagination.pageSize"
              :options="[5, 10, 20, 50, 100]"
              @input="getMeetings"
            ></b-select>
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="12">
        <b-table
          ref="meetings-table"
          class="agv-table td-middle"
          bordered
          :busy="isBusy"
          :items="tableData"
          :fields="fields"
          show-empty
        >
          <template #head(building)>
            <div class="d-flex align-items-middle">
              <span class="mr-2" @click="setSort('building.name')">{{
                $t("meetings.building")
              }}</span>
              <font-awesome-icon
                v-if="
                  tableHeaderSettings.sort.by == 'building.name' &&
                  tableHeaderSettings.sort.dir == 'desc'
                "
                icon="sort-up"
                @click="setSort('building.name', 'asc')"
              ></font-awesome-icon>
              <font-awesome-icon
                v-else-if="
                  tableHeaderSettings.sort.by == 'building.name' &&
                  tableHeaderSettings.sort.dir == 'asc'
                "
                icon="sort-down"
                @click="setSort('building.name', 'desc')"
              ></font-awesome-icon>
              <font-awesome-icon
                v-else
                icon="sort"
                @click="setSort('building.name')"
              ></font-awesome-icon>
            </div>
          </template>
          <template #head(name)>
            <div class="d-flex align-items-middle">
              <span class="mr-2" @click="setSort('name')">{{ $t("meetings.name") }}</span>
              <font-awesome-icon
                v-if="
                  tableHeaderSettings.sort.by == 'name' && tableHeaderSettings.sort.dir == 'desc'
                "
                icon="sort-up"
                @click="setSort('name', 'asc')"
              ></font-awesome-icon>
              <font-awesome-icon
                v-else-if="
                  tableHeaderSettings.sort.by == 'name' && tableHeaderSettings.sort.dir == 'asc'
                "
                icon="sort-down"
                @click="setSort('name', 'desc')"
              ></font-awesome-icon>
              <font-awesome-icon v-else icon="sort" @click="setSort('name')"></font-awesome-icon>
            </div>
          </template>
          <template #head(meeting_date)>
            <div class="d-flex align-items-middle">
              <span class="mr-2" @click="setSort('meeting_date')">{{ $t("meetings.date") }}</span>
              <font-awesome-icon
                v-if="
                  tableHeaderSettings.sort.by == 'meeting_date' &&
                  tableHeaderSettings.sort.dir == 'desc'
                "
                icon="sort-up"
                @click="setSort('meeting_date', 'asc')"
              ></font-awesome-icon>
              <font-awesome-icon
                v-else-if="
                  tableHeaderSettings.sort.by == 'meeting_date' &&
                  tableHeaderSettings.sort.dir == 'asc'
                "
                icon="sort-down"
                @click="setSort('meeting_date', 'desc')"
              ></font-awesome-icon>
              <font-awesome-icon
                v-else
                icon="sort"
                @click="setSort('meeting_date')"
              ></font-awesome-icon>
            </div>
          </template>
          <template #head(resolutions_count)>
            <div class="column-min-content">
              <span>Nombre de résolutions</span>
            </div>
          </template>
          <template #head(allowed_modalities)>
            <div class="column-min-content">
              <span>Modalités de vote autorisées</span>
            </div>
          </template>
          <template #table-busy>
            <appIsLoading />
          </template>
          <template v-slot:cell(building)="row">
            <b-row v-if="row.item.building" class="min-w-200 text-left">
              <b-col>
                {{ row.item.building.name }}
              </b-col>
            </b-row>
          </template>

          <template v-slot:cell(name)="row">
            <div class="text-left">
              {{ row.item.name }}
            </div>
          </template>

          <template v-slot:cell(resolutions_count)="row">
            <div class="text-center">
              {{ row.item.resolutions_count }}
            </div>
          </template>

          <template v-slot:cell(meeting_date)="row">
            <div class="text-left">
              {{
                row.item.meeting_date
                  ? $moment(row.item.meeting_date).locale("fr").format("DD MMMM YYYY HH:mm")
                  : null
              }}
            </div>
          </template>

          <template v-slot:cell(allowed_modalities)="row">
            <div
              v-for="(value, allowed_modality) in row.item.allowed_modalities"
              :key="allowed_modality"
              :class="{ 'd-none': !value }"
            >
              {{ allowed_modality }}
            </div>
          </template>

          <template v-slot:cell(_actions)="row">
            <b-row class="actions text-left min-w-180 nowrap">
              <b-col>
                <b-button
                  v-if="row.item.status < meetingStatuses.invitationSent"
                  size="sm"
                  title="Modifier la convocation"
                  class="tablebutton btn-edit"
                  @click="EDIT_MEETING(row.item)"
                >
                  <font-awesome-icon icon="pen" size="1x" />
                </b-button>

                <b-button
                  v-if="!row.item.building.has_building_owners"
                  class="tablebutton btn-edit"
                  title="Il n'y a pas de propriétaires d'immeubles pour cette réunion !"
                  variant="white"
                  @click="goToBuilding(row.item.building_id, 5, row.item.building.asl)"
                >
                  <font-awesome-icon class="text-warning" icon="exclamation-triangle" size="2x" />
                </b-button>

                <b-button
                  v-if="
                    row.item.building.has_building_owners &&
                    !row.item.building.has_charges_generales
                  "
                  class="tablebutton btn-edit"
                  title="Il n'y a pas de charges générales pour cette réunion !"
                  variant="white"
                  @click="goToBuilding(row.item.building_id, 7, row.item.building.asl)"
                >
                  <font-awesome-icon class="text-warning" icon="exclamation-triangle" size="2x" />
                </b-button>

                <b-button
                  v-if="
                    row.item.has_resolutions_without_keys &&
                    row.item.status == meetingStatuses.mandatesCreated
                  "
                  class="tablebutton btn-edit"
                  title="Chaque résolution doit avoir une clé de vote, merci de vérifier les résolutions !"
                  variant="white"
                  @click="goToMeeting(row.item.id, 3)"
                >
                  <font-awesome-icon class="text-warning" icon="exclamation-triangle" size="2x" />
                </b-button>

                <!-- NOTE: use this in case there where some errors when automatically creating mandates -->
                <b-button
                  v-if="
                    row.item.building.has_building_owners &&
                    row.item.building.has_charges_generales &&
                    row.item.status == meetingStatuses.created
                  "
                  size="sm"
                  variant="none"
                  class="tablebutton btn-green"
                  title="Créer les mandats"
                  @click="CREATE_MANDATES_FOR_MEETING(row.item)"
                >
                  <font-awesome-icon icon="vote-yea" size="1x" />
                </b-button>
                <b-button
                  v-if="
                    [
                      meetingStatuses.invitationSent,
                      meetingStatuses.invitationReadyToBeSent,
                      meetingStatuses.invitationInProgress,
                    ].includes(row.item.status)
                  "
                  size="sm"
                  title="Démarrer la convocation"
                  class="btn-edit tablebutton"
                  @click="GO_TO_MEETING_START(row.item)"
                >
                  <font-awesome-icon icon="play" size="1x" />
                </b-button>

                <b-button
                  v-if="[meetingStatuses.started].includes(row.item.status)"
                  size="sm"
                  title="Voir la convocation en cours"
                  class="btn-edit tablebutton"
                  @click="GO_TO_MEETING_START(row.item)"
                >
                  <font-awesome-icon icon="pause" size="1x" />
                </b-button>

                <b-button
                  v-if="
                    [
                      meetingStatuses.completed,
                      meetingStatuses.pvReadyToBeSent,
                      meetingStatuses.pvInProgress,
                    ].includes(row.item.status)
                  "
                  size="sm"
                  class="btn-edit tablebutton"
                  title="Voir l'historique de la convocation"
                  @click="GO_TO_CORRECT_MEETING_ERRORS(row.item)"
                >
                  <font-awesome-icon icon="history" size="1x" />
                </b-button>

                <b-button
                  v-if="row.item.status == meetingStatuses.created && !row.item.agenda_media_id"
                  size="sm"
                  title="Générer l'Ordre du Jour"
                  class="tablebutton btn-gray"
                  @click="GENERATE_PDF_PRIMARY_FUNCTION(row.item.id, GENERATE_PDF_ORDRE_DE_JOUR)"
                >
                  <font-awesome-icon icon="file-alt" class="color-gray" size="1x" />
                </b-button>

                <b-button
                  v-if="
                    row.item.building.has_building_owners &&
                    row.item.building.has_charges_generales &&
                    row.item.status > meetingStatuses.created &&
                    !row.item.has_resolutions_without_keys
                  "
                  size="sm"
                  title="Les documents PDF générés"
                  class="tablebutton btn-green"
                  @click="GO_TO_PDF_DOCUMENTS(row.item.id)"
                >
                  <font-awesome-icon icon="file-pdf" class="color-green" size="1x" />
                </b-button>

                <b-button
                  v-if="
                    row.item.status != meetingStatuses.archived &&
                    row.item.status != meetingStatuses.toBeDeleted
                  "
                  size="sm"
                  title="Dupliquer cette convocation"
                  class="btn-edit tablebutton"
                  @click="duplicateMeeting(row.item)"
                >
                  <font-awesome-icon icon="clone" size="1x" />
                </b-button>

                <b-button
                  v-if="row.item.status != meetingStatuses.toBeDeleted"
                  size="sm"
                  title="Supprimer la réunion"
                  class="tablebutton btn-red"
                  @click="openModalDeleteMeeting(row.item)"
                >
                  <font-awesome-icon icon="trash-alt" size="1x" />
                </b-button>
              </b-col>
            </b-row>
          </template>
          <template #empty>
            <span>{{ "Aucune donnée n'a été trouvée..." }}</span>
          </template>
        </b-table>
      </b-col>
      <b-col cols="12" class="text-center">
        <b-col cols="4" class="m-auto d-flex justify-content-center">
          <b-pagination
            v-model="pagination.page"
            :total-rows="pagination.rowCount"
            :per-page="pagination.pageSize"
            aria-controls="mt-table"
            @input="getMeetings()"
          ></b-pagination>
        </b-col>
      </b-col>
    </b-row>
    <b-modal id="modal-duplicate-meeting" hide-footer size="lg">
      <template #modal-title>
        <div>Dupliquer la convocation</div>
      </template>
      <modalDuplicateMeeting
        :meeting-to-duplicate="meetingToDuplicate"
        @closeModal="closeModalDuplicateMeeting"
      />
    </b-modal>
    <b-modal id="modal-delete-meeting" hide-footer size="lg">
      <template #modal-title>
        <div>Supprimer ou archiver la convocation</div>
      </template>
      <modalDeleteMeeting
        :meeting="meetingToDeleteOrArchive"
        @archive="archiveMeeting"
        @delete="deleteMeeting"
        @close="closeModalDeleteMeeting"
      />
    </b-modal>
  </b-container>
</template>

<script>
import MeetingMW from "@/middlewares/Meeting";
import MeetingORM from "@/store/orm-axios/models/Meeting";
import MediaMW from "@/middlewares/Media";
import PDFMW from "@/middlewares/PDF";
import useAlerts from "@/use/useAlerts.js";
import usePDFs from "@/use/usePDFs.js";
import ConfigurationMW from "@/middlewares/Configuration";
import ConfigurationORM from "@/store/orm-axios/models/Configuration";
import modalDuplicateMeeting from "@/components/modals/duplicate-meeting/DuplicateMeeting";
import modalDeleteMeeting from "@/components/modals/DeleteMeeting";

export default {
  components: {
    modalDuplicateMeeting,
    modalDeleteMeeting,
    appIsLoading: () => import("@/components/snippets/IsLoading"),
  },
  props: {
    displayArchivedMeetings: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modify: false,
      pagination: {
        page: 1,
        pageSize: 10,
        rowCount: 0,
        pageCount: 0,
      },
      tableData: [],
      isBusy: true,
      totalRows: 0,
      pageSize: 10,
      page: 1,
      fields: [
        {
          key: "building",
          label: "Immeuble",
        },
        {
          key: "name",
          label: "Nom convocation",
        },
        {
          key: "meeting_date",
          label: "Date",
        },
        {
          key: "resolutions_count",
          label: "Nombre de résolutions",
        },
        {
          key: "allowed_modalities",
          label: "Modalités de vote autorisées",
        },
        {
          key: "_actions",
          label: "Actions",
        },
      ],
      tableHeaderSettings: {
        filter: [
          {
            column: "name",
            value: "",
          },
        ],
        // NOTE: This rule applies below on computed property meetings
        sort: {
          by: "meeting_date",
          dir: "desc",
        },
        selectedMeeting: false,
      },
      pdfFooterMessage: "",
      pdfContactDetails: "",
      meetingToDuplicate: null,
      meetingToDeleteOrArchive: null,
      searchParams: "",
      isSearchInputFocused: false,
      hasSearchInputBeenReseted: true,
    };
  },
  computed: {
    meetingsInProgress() {
      return MeetingORM.query().where("status", this.meetingStatuses.started).get();
    },
    configuration() {
      // There will be only one configuration for each agency id
      return ConfigurationORM.query().where("agency_id", this.agencyId).first();
    },
    allCountries() {
      return this.$store.getters.getCountries;
    },
    // NOTE: When change orderBy make sure to change in the tableHeaderSettings.sort
    meetings() {
      return MeetingORM.query()
        .where("agency_id", this.agencyId)
        .orderBy("meeting_date", "desc")
        .withAllRecursive(2)
        .get();
    },
    timezone() {
      return localStorage.getItem("timezone");
    },
  },
  watch: {
    configuration() {
      this.getPdfConfigs();
    },
    meetings(newValue) {
      this.tableData = newValue;
    },
    displayArchivedMeetings(seeArchive) {
      this.filterByArchivedStatus(seeArchive, true);
    },
  },
  async created() {
    this.isBusy = true;
    this.filterByArchivedStatus(this.displayArchivedMeetings, false);
    await this.getMeetings().catch(err => {
      console.error(err);
    });
    this.isBusy = false;
    ConfigurationMW.FETCH(this.agencyId).then(() => {
      this.getPdfConfigs();
    });
    if (this.timezone) {
      this.$moment.tz.setDefault(this.timezone);
    }
  },
  methods: {
    prepareQueryParams() {
      var queryParams = {
        filter: [],
        sort: {},
      };
      if (this.tableHeaderSettings.sort.by && this.tableHeaderSettings.sort.dir) {
        queryParams.sort["by"] = this.tableHeaderSettings.sort.by;
        queryParams.sort["dir"] = this.tableHeaderSettings.sort.dir;
      }
      this.tableHeaderSettings.filter.forEach(filter => {
        if (!!filter.value || filter.value === 0) {
          var preparedFilter = {
            column: filter.column,
            value: filter.value,
            // [filter.column]: filter.value,
          };
          queryParams.filter.push(preparedFilter);
        }
      });
      return queryParams;
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.page = 1;
    },
    async getMeetings() {
      this.isBusy = true;
      await MeetingORM.deleteAll();
      var queryParams = this.prepareQueryParams();
      const meetingProperties = [
        "id",
        "name",
        "address",
        "meetingDate",
        "resolutionsCount",
        "hasResolutionsWithoutKeys",
        "allowedModalities",
        "status",
        "buildingId",
        "agendaMediaId",
        "agencyId",
        "mandates",
        // "freezed_meeting_details", ???
        "created",
        "externalVideoSolution",
        "access",
        "showConnectionDetails",
        "votesNeedRecalculations",
        "isScheduledToGeneratePdfs",
        "generatePdfsProgress",
        {
          building: ["id", "name", "hasBuildingOwners", "hasChargesGenerales", "asl"],
        },
      ];
      return MeetingMW.FETCH_BY_PAGE(queryParams, this.pagination, meetingProperties)
        .then(res => {
          if (!process.env.VUE_APP_IS_WEB) {
            if (res.models.length == 0 && res.pagination.rowCount > 0) {
              this.pagination.page = 1;
            }
            this.pagination = res.pagination;
            this.tableData = res.serialize();
          } else {
            let totalItems = res.response.data["hydra:totalItems"];
            this.pagination.rowCount = totalItems;
            this.tableData = res.response.data["hydra:member"];
          }

          this.isBusy = false;
        })
        .catch(err => {
          this.isBusy = false;
        });
    },
    GO_TO_PDF_DOCUMENTS(meetingId) {
      this.isBusy = true;
      this.$router.push({
        name: "meeting-documents",
        params: {
          id: meetingId,
        },
      });
    },
    GO_TO_MEETING_START(meeting) {
      // TODO: Check if another meeting is not in progress
      // var self = this;
      // if (
      //   this.meetingsInProgress &&
      //   this.meetingsInProgress.length > 0 &&
      //   this.meetingsInProgress[0].id != meeting.id
      // ) {
      //   self
      //     .$alert("", self.$t("meetings.another_in_progress"), "warning")
      //     .then(res => {
      //       return;
      //     })
      //     .catch(err => {
      //       console.log("err", err);
      //     });
      // } else {
      //   this.isBusy = true;

      if (
        meeting.status == this.meetingStatuses.invitationSent ||
        meeting.status == this.meetingStatuses.invitationReadyToBeSent ||
        meeting.status == this.meetingStatuses.invitationInProgress
      ) {
        this.$router.push({ name: "meeting-agenda", params: { id: meeting.id } });
      } else {
        this.$router.push({ name: "meeting-start", params: { id: meeting.id } });
      }
    },
    GO_TO_CORRECT_MEETING_ERRORS(meeting) {
      this.isBusy = true;
      this.$router.push({ name: "meeting-start", params: { id: meeting.id } });
    },
    async EDIT_MEETING(meeting) {
      this.isBusy = true;
      this.$router.push({ name: "edit-meeting", params: { id: meeting.id } });
    },
    async DELETE_MEETING(meeting) {
      console.log("DELETE_MEETING");
      var self = this;
      self
        .$swal(self.alertQuestion())
        .then(res => {
          if (res.value) {
            // self.isLoadingDelete = true;
            MeetingMW.DELETE(meeting)
              .then(() => {
                // self.isLoadingDelete = false;
                var index = self.$_.findIndex(self.tableData, { id: meeting.id });
                console.log("index", index);
                // var data = self.tableData.splice(index, 1);
                // console.log("data", data);
                var data = JSON.parse(JSON.stringify(self.tableData));
                data = self.$_.filter(data, item => {
                  return item.id != meeting.id;
                });
                console.log("data", data);
                self.tableData = data;
                self.$refs["meetings-table"].refresh();
              })
              .catch(err => {
                // self.isLoadingDelete = false;
                console.log("error", err);
                self.$swal(self.alertError());
              });
          } else {
            // console.log("Cancel");
          }
        })
        .catch(err => {
          console.log("error", err);
          self.$swal(self.alertError());
        });
    },
    async CREATE_MANDATES_FOR_MEETING(meeting) {
      var index = this.$_.findIndex(this.tableData, { id: meeting.id });
      this.$store.state.showLoaderOverlay = true;
      MeetingMW.CREATE_MANDATES_FOR_MEETING(meeting)
        .then(res => {
          this.$store.state.showLoaderOverlay = false;
          if (index != -1 && index != undefined) {
            let updatedMeeting = MeetingORM.find(meeting.id);
            this.tableData[index]["status"] = updatedMeeting.status;
          }
          // console.log("res create manates", res);
        })
        .catch(err => {
          this.$store.state.showLoaderOverlay = false;
          console.log("error create manates", err);
          this.$swal(this.alertError());
        });
    },
    // use this to not repeat first steps for every function
    GENERATE_PDF_PRIMARY_FUNCTION(meetingId, generatePdfSecondaryFunction) {
      // check if the Agency has a logo
      const idLogoAgency = localStorage.getItem("userMediaId");
      if (idLogoAgency) {
        this.$store.state.showLoaderOverlay = true;
        MeetingMW.GET_WITH_RELATED_MEDIA(meetingId)
          .then(meeting => {
            MediaMW.GET_LOGO_IN_BASE64(idLogoAgency)
              .then(logo => {
                meeting["logoInBase64"] = logo["file_as_base64_string"];

                // call secondary function
                generatePdfSecondaryFunction(meeting);
              })
              .catch(err => {
                console.error(err);
                this.$store.state.showLoaderOverlay = false;
                const { alertErrorAndReport } = useAlerts();
                alertErrorAndReport(
                  err,
                  "Impossible d'extraire le logo !",
                  "Veuillez vérifier votre connexion Internet."
                );
              });
          })
          .catch(err => {
            console.error(err);
            this.$store.state.showLoaderOverlay = false;
            const { alertErrorAndReport } = useAlerts();
            alertErrorAndReport(
              err,
              "Impossible d'extraire les informations sur la Convocation !",
              "Veuillez vérifier votre connexion Internet."
            );
          });
      } else {
        this.$swal({
          icon: "warning",
          title: "Vous n'avez spécifié aucun logo pour votre agence !",
          html: "Accédez au profil et ajoutez un logo d'agence.",
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: "Ajouter le logo",
          confirmButtonClass: "btn btn-primary btn-lg",
          cancelButtonText: "Pas maintenant",
        }).then(res => {
          if (res.value) {
            this.$router.push({
              name: "profile",
            });
          }
        });
      }
    },
    GENERATE_PDF_ORDRE_DE_JOUR(meeting) {
      const { generateTemplateOrder, downloadDialog } = usePDFs();

      generateTemplateOrder(meeting, this.pdfFooterMessage).then(template => {
        PDFMW.MEETING_PDF_CREATE_RETURN(meeting.id, {
          order: template,
        })
          .then(pvPDF => {
            downloadDialog(pvPDF.response.data.document, "ODJ " + meeting.name, "pdf");
            this.$store.state.showLoaderOverlay = false;
          })
          .catch(err => {
            this.generatePdfCommonError(err);
          });
      });
    },
    generatePdfCommonError(err) {
      console.error(err);
      this.$store.state.showLoaderOverlay = false;
      const { alertErrorAndReport } = useAlerts();
      alertErrorAndReport(err, "", "");
    },
    setSort(by, dir) {
      if (!dir) {
        if (this.tableHeaderSettings.sort.dir == "asc") {
          dir = "desc";
        } else {
          dir = "asc";
        }
      }
      console.log("by", by);
      console.log("dir", dir);
      this.tableHeaderSettings.sort.by = by;
      this.tableHeaderSettings.sort.dir = dir;
      console.log(this.tableHeaderSettings.sort);
      this.getMeetings();
    },
    getPdfConfigs() {
      // Get current pdf_configs value
      if (this.configuration && this.configuration.settings.length > 0) {
        this.configuration.settings.forEach(element => {
          if (element.pdf_footer) {
            this.pdfFooterMessage = element.pdf_footer;
          }
          if (element.pdf_contact_details) {
            this.pdfContactDetails = element.pdf_contact_details;
          }
        });
      }
    },
    closeModalDuplicateMeeting() {
      this.$bvModal.hide("modal-duplicate-meeting");
    },
    duplicateMeeting(meeting) {
      this.meetingToDuplicate = meeting;
      this.$bvModal.show("modal-duplicate-meeting");
    },
    goToBuilding(building_id, stepNr = false, asl) {
      if (asl == 1) {
        this.$router.push({ name: "edit-building-asl", params: { id: building_id, step: stepNr } });
      } else {
        this.$router.push({ name: "edit-building", params: { id: building_id, step: stepNr } });
      }
    },
    goToMeeting(meeting_id, stepNr = false) {
      this.$router.push({ name: "edit-meeting", params: { id: meeting_id, step: stepNr } });
    },
    search() {
      if (this.isBusy) return;

      this.hasSearchInputBeenReseted = false;

      this.tableHeaderSettings.filter[0].value = this.searchParams;
      this.getMeetings();
    },
    focusSearch() {
      this.isSearchInputFocused = true;
    },
    unfocusSearch() {
      this.isSearchInputFocused = false;
    },
    resetSearch() {
      this.hasSearchInputBeenReseted = true;
      this.isSearchInputFocused = false;

      this.searchParams = "";
      this.tableHeaderSettings.filter[0].value = this.searchParams;
      this.getMeetings();
    },
    openModalDeleteMeeting(meeting) {
      this.meetingToDeleteOrArchive = meeting;
      this.$bvModal.show("modal-delete-meeting");
    },
    closeModalDeleteMeeting() {
      this.meetingToDeleteOrArchive = null;
      this.$bvModal.hide("modal-delete-meeting");
    },
    archiveMeeting() {
      this.$store.state.showLoaderOverlay = true;
      let userMediaId = localStorage.getItem("userMediaId");
      if (userMediaId === null) {
        this.$store.state.showLoaderOverlay = false;
        this.closeModalDeleteMeeting();
        this.$swal(this.alertError("Veuillez vous assurer que l'agence a un logo mis en place!"));
      } else {
        MeetingMW.ARCHIVE(this.meetingToDeleteOrArchive, userMediaId)
          .then(res => {
            this.closeModalDeleteMeeting();
            this.getMeetings();
            this.$store.state.showLoaderOverlay = false;
          })
          .catch(err => {
            this.closeModalDeleteMeeting();
            this.$store.state.showLoaderOverlay = false;
          });
      }
    },
    deleteMeeting(doZipBackup) {
      this.$store.state.showLoaderOverlay = true;
      MeetingMW.DELETE(this.meetingToDeleteOrArchive, doZipBackup)
        .then(res => {
          this.closeModalDeleteMeeting();
          this.getMeetings();
          this.$store.state.showLoaderOverlay = false;
        })
        .catch(err => {
          this.closeModalDeleteMeeting();
          this.$store.state.showLoaderOverlay = false;
        });
    },
    filterByArchivedStatus(seeArchive, callApi) {
      if (seeArchive) {
        this.tableHeaderSettings.filter.push({
          column: "status",
          value: this.meetingStatuses.archived,
        });
      } else {
        this.tableHeaderSettings.filter = this.$_.without(
          this.tableHeaderSettings.filter,
          this.$_.findWhere(this.tableHeaderSettings.filter, {
            column: "status",
          })
        );
      }
      if (callApi) {
        this.getMeetings();
      }
    },
  },
};
</script>
